import { ClassNames } from '@emotion/core'
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Paginate from 'react-paginate'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Margin } from 'shared/components'

import { PageSize } from './components/pageSize'
import * as styles from './styles'

const getActivePageIndex = ({ forcePage, tableState, page }) => {
  if (forcePage !== undefined) {
    return forcePage
  }

  if (tableState?.page !== undefined) {
    return tableState.page
  }

  return page
}

const PaginationComponent = ({
  page,
  loading,
  totalPages,
  showPagination,
  showPageSizeOptions,
  pageSize,
  pageSizeOptions,
  onRequestPageChange,
  onPageSizeChange,
  forcePage,
  tableState,
}) => {
  // Local state to track the last forced page
  const [lastForcedPage, setLastForcedPage] = useState(forcePage);

  // Update lastForcedPage when forcePage changes
  useEffect(() => {
    if (forcePage !== undefined && forcePage !== lastForcedPage) {
      setLastForcedPage(forcePage);
    }
  }, [forcePage, lastForcedPage]);

  // Determine which page index to use
  // Priority: 1. forcePage (from container), 2. tableState.page, 3. component prop page
  const activePageIndex = getActivePageIndex({ forcePage, tableState, page })

  // Make sure page is within valid range
  const safePageIndex = Math.max(0, Math.min(activePageIndex, totalPages - 1));

  // Custom handler to ensure page changes are properly tracked
  const handlePageChange = (data) => {
    if (onRequestPageChange) {
      onRequestPageChange(data);
    }
  };

  return (
    <div css={styles.container}>
      {totalPages > 1 && showPagination && !loading && (
        <ClassNames>
          {({ css }) => (
            <Paginate
              containerClassName={css(styles.paginationContainer)}
              activeClassName={css(styles.paginatorActivePage)}
              breakClassName={css(styles.paginatorItem)}
              previousLinkClassName={css(styles.paginatorLink)}
              nextLinkClassName={css(styles.paginatorLink)}
              pageLinkClassName={css(styles.paginatorLink)}
              disabledClassName={css(styles.paginatorItemDisabled)}
              previousLabel={<FontAwesomeIcon icon="caret-left" />}
              nextLabel={<FontAwesomeIcon icon="caret-right" />}
              forcePage={safePageIndex}
              pageCount={totalPages}
              pageRangeDisplayed={5}
              marginPagesDisplayed={2}
              onPageChange={handlePageChange}
            />
          )}
        </ClassNames>
      )}
      {showPageSizeOptions && !loading && (
        <Margin inline left={2}>
          <PageSize
            pageSize={pageSize}
            pageSizeOptions={pageSizeOptions}
            onPageSizeChange={onPageSizeChange}
          />
        </Margin>
      )}
    </div>
  )
}

PaginationComponent.propTypes = {
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  showPagination: PropTypes.bool.isRequired,
  showPageSizeOptions: PropTypes.bool.isRequired,
  pageSize: PropTypes.number.isRequired,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onRequestPageChange: PropTypes.func.isRequired,
  forcePage: PropTypes.number,
  tableState: PropTypes.object,
}

export default PaginationComponent
